import { Suspense, useEffect } from 'react';
import { useAuth } from './context/AuthContext';
import { useI18n } from './context/I18nContext';
import { useRoute, navigate } from './shared/lib/router';
import AppShell from './app-shell/AppShell';
import LoginPage from './pages/LoginPage';
import PasswordResetRequestPage from './pages/PasswordResetRequestPage';
import PasswordResetConfirmPage from './pages/PasswordResetConfirmPage';
import SecuritySettingsPage from './pages/SecuritySettingsPage';
import NotificationsPage from './pages/NotificationsPage';
import SearchResultsPage from './pages/SearchResultsPage';
import { moduleRegistry } from './modules/registry';

function RedirectTo({ path }) {
  useEffect(() => {
    navigate(path, { replace: true });
  }, [path]);
  return null;
}

function findActiveModule(pathname) {
  return moduleRegistry.find((mod) => pathname === mod.path || pathname.startsWith(`${mod.path}/`));
}

function canAccessModule(mod, enabledModules, permissions) {
  if (!mod) return false;
  const moduleOn = enabledModules.includes(mod.key);
  const permissionOk = !mod.requiredPermission || permissions.includes(mod.requiredPermission);
  return moduleOn && permissionOk;
}

export default function App() {
  const { isAuthenticated, initializing, enabledModules, permissions } = useAuth();
  const { t } = useI18n();
  const pathname = useRoute();

  if (initializing) {
    return (
      <div className="app-loading-screen" role="status">
        {t('common', 'actions.loading')}
      </div>
    );
  }

  if (!isAuthenticated) {
    // Accepting an invite and confirming a password reset are the same
    // backend action (POST /auth/password-reset/confirm) -- an invited
    // user's password_hash is just a random placeholder they've never seen,
    // so "set your password" and "reset your password" are functionally
    // identical. Only the copy differs (isInvite swaps a few strings).
    if (pathname.startsWith('/accept-invite')) return <PasswordResetConfirmPage isInvite />;
    if (pathname.startsWith('/password-reset/confirm')) return <PasswordResetConfirmPage />;
    if (pathname.startsWith('/password-reset')) return <PasswordResetRequestPage />;
    return <LoginPage />;
  }

  // Account-level pages (not gated by module/permission -- every
  // authenticated user manages their own security settings regardless of
  // which business modules their tenant has enabled) are handled before
  // the module lookup below.
  if (pathname === '/security' || pathname.startsWith('/security/')) {
    return (
      <AppShell activeModule={null} pathname={pathname}>
        <SecuritySettingsPage />
      </AppShell>
    );
  }

  if (pathname === '/notifications' || pathname.startsWith('/notifications/')) {
    return (
      <AppShell activeModule={null} pathname={pathname}>
        <NotificationsPage />
      </AppShell>
    );
  }

  if (pathname === '/search') {
    return (
      <AppShell activeModule={null} pathname={pathname}>
        <SearchResultsPage />
      </AppShell>
    );
  }

  const activeModule = findActiveModule(pathname);
  let content = null;

  if (!activeModule) {
    const firstAccessible = moduleRegistry.find((mod) => canAccessModule(mod, enabledModules, permissions));
    content = firstAccessible ? (
      <RedirectTo path={firstAccessible.path} />
    ) : (
      <div className="empty-state">{t('common', 'table.noResults')}</div>
    );
  } else if (!canAccessModule(activeModule, enabledModules, permissions)) {
    content = <div className="empty-state">{t('common', 'errorBanner')}</div>;
  } else {
    const PageComponent = activeModule.component;
    content = (
      <Suspense fallback={<div className="app-loading-screen">{t('common', 'actions.loading')}</div>}>
        <PageComponent />
      </Suspense>
    );
  }

  return (
    <AppShell activeModule={activeModule} pathname={pathname}>
      {content}
    </AppShell>
  );
}
